import type { ProductTemplate } from './types';
import { traitsOf } from './categories';

/** A catalogue product's Height from ground, as the app, the desktop store and the
 *  server read and write it. Pure, so all three share it. The products whose
 *  copies have always started at it, 150 when it gives none (a heater, a shower,
 *  an infrared panel), are their category's `liftedByDefault`. */

type Legacy = { heightFromGround?: number; heaterHeightFromGround?: unknown };

/** Read a catalogue product saved before its Height from ground had one name: its
 *  own and each part's `heaterHeightFromGround` become `heightFromGround`. Only
 *  the products that started at it keep it (`liftedByDefault`), so a value left
 *  on any other product does not lift its next copies. A value already under the
 *  new name wins. In place; true when anything changed. */
export function migrateProductHeightFromGround(t: ProductTemplate): boolean {
  const keep = traitsOf(t.category).liftedByDefault;
  let changed = false;
  for (const o of [t, ...(t.parts ?? [])] as Legacy[]) {
    if (!('heaterHeightFromGround' in o)) continue;
    if (keep && o.heightFromGround == null && typeof o.heaterHeightFromGround === 'number') o.heightFromGround = o.heaterHeightFromGround;
    delete o.heaterHeightFromGround;
    changed = true;
  }
  return changed;
}

/** What a product is stored as: itself plus, for a product `liftedByDefault`, its
 *  and its parts' Height from ground under the old name too, so a build from
 *  before the rename still places it at the right height. The extra key comes
 *  last and `migrateProductHeightFromGround` drops it on read, so the product
 *  read back is the one written, key for key. Drop once those builds are gone. */
export function withLegacyHeightFromGround(t: ProductTemplate): ProductTemplate {
  if (!traitsOf(t.category).liftedByDefault) return t;
  const legacy = <T extends { heightFromGround?: number }>(o: T): T => (o.heightFromGround == null ? o : { ...o, heaterHeightFromGround: o.heightFromGround });
  const out = legacy(t);
  return t.parts ? { ...out, parts: t.parts.map(legacy) } : out;
}
